import { Component, OnInit, OnDestroy, Injector, inject } from '@angular/core';
import { ActivatedRoute, Router, ActivationEnd } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  AlfrescoApiService,
  FileUploadErrorEvent,
} from '@alfresco/adf-content-services';
import {
  DiscoveryEntry,
  GroupEntry,
  Group,
  UserRepresentation,
} from '@alfresco/js-api';

import { AppStore } from '../../../../app/store/states/app.state';
import { WkPageTitleService } from '../../../services/wk-page-title.service';
import { WkAppConfigService } from '../../../services/wk-app-config.service';
import { WkAlfrescoApiService } from '../../../services/wk-alfresco-api.service';
import { WkAuthenticationService } from '../../../services/wk-authentication.service';
import { WkRouterExtensionService } from '../../../services/wk-router-extension.service';
import { WkContentApiService } from '../../../services/wk-content-api.service';

import { WkSettingsService, WkSharedLinksApiService } from '../../../services';
import {
  SetBpmUserAction,
  SetCurrentUrlAction,
  SetRepositoryInfoAction,
  SetUserProfileAction,
  SnackbarErrorAction,
  GetFeatureFlags,
} from '../../../store/actions/app.custom-actions';
import { WkAlfrescoApplicationService } from '../../../services/wk-alfresco-application.service';
import { WKFileUploadService } from '../../../services/file-upload.service';
import { OverlayContainer } from '@angular/cdk/overlay';
import { PeopleProcessService } from '@alfresco/adf-process-services';
import {
  AppService,
  UserProfileService,
} from '@adw/projects/aca-shared/src/public-api';

@Component({
  selector: 'app-root',
  templateUrl: './wk-app.component.html',
  styleUrls: ['./wk-app.component.scss'],
})
export class WkAppComponent implements OnInit, OnDestroy {
  onDestroy$: Subject<boolean> = new Subject<boolean>();
  pageHeading = '';
  private readonly route: ActivatedRoute;
  private readonly router: Router;
  private readonly store: Store<AppStore>;
  private readonly pageTitle: WkPageTitleService;
  private readonly config: WkAppConfigService;
  private readonly apiService: WkAlfrescoApiService;
  private readonly authenticationService: WkAuthenticationService;
  private readonly routerExtensionService: WkRouterExtensionService;
  private readonly contentApi: WkContentApiService;
  private readonly appService: AppService;
  private readonly sharedLinksApiService: WkSharedLinksApiService;
  private readonly wkApplicationService: WkAlfrescoApplicationService;
  private readonly wkFileUploadService: WKFileUploadService;
  private readonly coreAlfrescoApiService: AlfrescoApiService;
  private readonly overlayContainer: OverlayContainer;
  private readonly peoplProcessService: PeopleProcessService;
  private readonly appSettingsService: WkSettingsService =
    inject(WkSettingsService);
  private readonly userProfileService: UserProfileService =
    inject(UserProfileService);
  constructor(injector: Injector) {
    this.route = injector.get<ActivatedRoute>(ActivatedRoute);
    this.router = injector.get<Router>(Router);
    this.store = injector.get<Store<AppStore>>(Store);
    this.config = injector.get<WkAppConfigService>(WkAppConfigService);
    this.apiService = injector.get<WkAlfrescoApiService>(WkAlfrescoApiService);
    this.authenticationService = injector.get<WkAuthenticationService>(
      WkAuthenticationService
    );
    this.routerExtensionService = injector.get<WkRouterExtensionService>(
      WkRouterExtensionService
    );
    this.contentApi = injector.get<WkContentApiService>(WkContentApiService);
    this.appService = injector.get<AppService>(AppService);
    this.sharedLinksApiService = injector.get<WkSharedLinksApiService>(
      WkSharedLinksApiService
    );
    this.wkApplicationService = injector.get<WkAlfrescoApplicationService>(
      WkAlfrescoApplicationService
    );
    this.wkFileUploadService =
      injector.get<WKFileUploadService>(WKFileUploadService);
    this.pageTitle = injector.get<WkPageTitleService>(WkPageTitleService);
    window['_datePipe'] = injector.get<DatePipe>(DatePipe);
    this.coreAlfrescoApiService =
      injector.get<AlfrescoApiService>(AlfrescoApiService);
    this.overlayContainer = injector.get<OverlayContainer>(OverlayContainer);
    this.peoplProcessService =
      injector.get<PeopleProcessService>(PeopleProcessService);
  }

  ngOnInit() {
    this.apiService
      .getInstance()
      .on('error', (error: { status: number; response: any }) => {
        if (
          error.status === 401 &&
          !this.coreAlfrescoApiService.isExcludedErrorListener(
            error?.response?.req?.url
          ) &&
          !this.authenticationService.isLoggedIn()
        ) {
          let redirectUrl = this.route.snapshot.queryParams['redirectUrl'];
          if (!redirectUrl) {
            redirectUrl = this.router.url;
          }
          this.router.navigate(['/login'], {
            queryParams: { redirectUrl },
          });
        }
      });

    this.loadCustomCss();
    this.loadCustomWebFont();

    this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof ActivationEnd &&
            event.snapshot.children.length === 0
        )
      )
      .subscribe((event: ActivationEnd) => {
        const snapshot: any = event.snapshot || {};
        const data: any = snapshot.data || {};
        this.pageHeading = data.title || '';
        this.pageTitle.setTitle(data.title || '');
        this.store.dispatch(new SetCurrentUrlAction(this.router.url));
      });
    this.routerExtensionService.mapExtensionRoutes();
    this.wkFileUploadService.fileUploadError.subscribe((error) =>
      this.onFileUploadedError(error)
    );
    this.sharedLinksApiService.error
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((err: { message: string }) => {
        this.store.dispatch(new SnackbarErrorAction(err.message));
      });
    this.appService.ready$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isReady) => {
        if (isReady) {
          this.loadRepositoryStatus();
          this.loadUserProfile();
          this.loadApsUserProfile();
        }
      });

    this.overlayContainer.getContainerElement().setAttribute('role', 'region');
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  private loadRepositoryStatus() {
    this.contentApi
      .getRepositoryInformation()
      .subscribe((response: DiscoveryEntry) => {
        this.store.dispatch(
          new SetRepositoryInfoAction(response.entry.repository)
        );
      });
  }

  protected async loadUserProfile() {
    this.userProfileService.loadUserProfile().catch(() => {
      /* profile BehaviorSubject stays null */
    });
    const groupsEntries: GroupEntry[] =
      await this.wkApplicationService.groupInstance.listAllGroupMembershipsForPerson(
        '-me-',
        { maxItems: 250 }
      );
    const groups: Group[] = [];
    if (groupsEntries) {
      groups.push(...groupsEntries.map((obj) => obj.entry));
    }
    this.contentApi.getPerson('-me-').subscribe((person) => {
      if (person) {
        this.store.dispatch(
          new GetFeatureFlags({
            person: person.entry,
            groups: groups
              .map((group) => group.id)
              .filter((id): id is string => id !== undefined),
          })
        );
        this.store.dispatch(
          new SetUserProfileAction({ person: person.entry, groups })
        );
      }
    });
  }

  onFileUploadedError(error: FileUploadErrorEvent) {
    let message = 'APP.MESSAGES.UPLOAD.ERROR.GENERIC';
    if (error.error.status === 403) {
      message = 'APP.MESSAGES.UPLOAD.ERROR.403';
    }
    if (error.error.status === 404) {
      message = 'APP.MESSAGES.UPLOAD.ERROR.404';
    }
    if (error.error.status === 409) {
      message = 'APP.MESSAGES.UPLOAD.ERROR.CONFLICT';
    }
    if (error.error.status === 500) {
      message = 'APP.MESSAGES.UPLOAD.ERROR.500';
    }
    if (error.error.status === 504) {
      message = 'APP.MESSAGES.UPLOAD.ERROR.504';
    }
    this.store.dispatch(new SnackbarErrorAction(message));
  }

  private loadCustomCss(): void {
    const customCssPath = this.appSettingsService.customCssPath;
    if (customCssPath) {
      this.createLink(customCssPath);
    }
  }

  private loadCustomWebFont(): void {
    const webFontPath = this.appSettingsService.webFontPath;
    if (webFontPath) {
      this.createLink(webFontPath);
    }
  }

  protected async loadApsUserProfile(): Promise<void> {
    if (!this.config.apsEnabled()) {
      return;
    }

    try {
      const userInfo: UserRepresentation = await firstValueFrom(
        this.peoplProcessService.getCurrentUserInfo()
      );

      if (userInfo) {
        this.store.dispatch(new SetBpmUserAction({ bpmUser: userInfo }));
      } else {
        console.warn('APS profile request returned an empty response');
      }
    } catch (error) {
      console.error('APS profile load failed', error);
    }
  }

  private createLink(url: string): void {
    const cssLinkElement = document.createElement('link');
    cssLinkElement.setAttribute('rel', 'stylesheet');
    cssLinkElement.setAttribute('type', 'text/css');
    cssLinkElement.setAttribute('href', url);
    document.head.appendChild(cssLinkElement);
  }
}
